/**
 * 
 * Usage: 
 *  AppDialogHelper.show('Title', 'Description', [
        {
            title: 'Yes', type: 'positive', onClick: () => {
                AppDialogHelper.hide();
            }
        },
        {
            title: 'Cancel', type: 'neutral', onClick: () => {
                AppDialogHelper.hide();
            }
        },
        {
            title: 'No', type: 'negative', onClick: () => {
                AppDialogHelper.hide();
            }
        }
    ]);
 * 
 */

import AppColors from 'general/constants/AppColors';
import Utils from 'general/utils/Utils';
import { createRef, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import KTBSButton from '../OtherKeenComponents/KTBSButton';

const ref = createRef();

export const AppDialogButtonType = {
  positive: 'positive',
  negative: 'negative',
  neutral: 'neutral',
  warning: 'warning',
};

// MARK: --- Helper functions ---
function getButtonVariant(type) {
  let buttonVariant = 'success';
  if (type === 'negative') {
    buttonVariant = 'secondary';
  }
  if (type === 'neutral') {
    buttonVariant = 'secondary';
  }
  return buttonVariant;
}

function getButtonColor(type) {
  let buttonVariant = 'success';
  if (type === 'negative') {
    buttonVariant = 'danger';
  }
  if (type === 'neutral') {
    buttonVariant = 'secondary';
  }
  if (type === 'warning') {
    buttonVariant = 'warning';
  }
  return buttonVariant;
}

function getButtonTitleColor(type) {
  let titleColor = 'white';
  if (type === 'neutral') {
    titleColor = 'rgba(74, 86, 119, 1)';
  }
  return titleColor;
}

function AppDialog(props) {
  // MARK: --- Params ---
  const [dialogShowing, setDialogShowing] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [buttons, setButtons] = useState([]);
  const [canClickOutsideToDismiss, setCanClickOutsideToDismiss] = useState(true);
  const [icon, setIcon] = useState('');
  const [customContent, setCustomContent] = useState(null);
  const [size, setSize] = useState('');
  const [loading, setLoading] = useState(false);

  // MARK: --- Functions ---
  function handleClose() {
    setDialogShowing(false);
    setLoading(false);
  }

  // MARK: --- Hooks ---
  useEffect(() => {
    ref.current = {
      show: (title, description, buttons, canClickOutsideToDismiss, icon, customContent, size, loading) => {
        console.log('check loading: ', loading);
        setDialogShowing(true);
        setTitle(title);
        setDescription(description);
        setButtons(buttons);
        setCanClickOutsideToDismiss(canClickOutsideToDismiss);
        setIcon(icon);
        setCustomContent(customContent);
        setSize(size);
        setLoading(loading);
      },
      hide: () => {
        setDialogShowing(false);
      },
    };

    return () => { };
  }, []);

  return (
    <div>
      <Modal
        className="AppDialog"
        show={dialogShowing}
        onHide={handleClose}
        centered
        size={size}
        contentClassName="rounded-lg"
        backdrop={canClickOutsideToDismiss ? 'static' : true}
        onEntered={() => {
          Utils.lockedBodyScroll();
        }}
        onExited={() => {
          Utils.unlockedBodyScroll();
        }}
      >
        {/* Header */}
        {/* <Modal.Header className="px-5 py-5">
          <Modal.Title>{title}</Modal.Title>
          <div
            className="btn btn-xs btn-icon btn-light btn-hover-secondary cursor-pointer"
            onClick={() => {
              handleClose();
            }}
          >
            <i className="far fa-times"></i>
          </div>
        </Modal.Header> */}

        {/* Body */}
        <Modal.Body>
          <div className="d-flex flex-column align-items-center gap-2">
            {/* icon */}
            {icon && <img className="max-w-100 mt-2 mb-4" src={icon} />}
            {title && (
              <span
                style={{
                  color: AppColors.gray[800],
                  fontWeight: '600',
                  fontSize: Utils.returnFontSize(16),
                }}
              >
                {title}
              </span>
            )}
            {description && (
              <span
                style={{
                  color: AppColors.gray[600],
                  fontWeight: '400',
                  fontSize: Utils.returnFontSize(14),
                  textAlign: 'center',
                }}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
            {/* custom content */}
            {customContent && <div className="w-100">{customContent}</div>}
          </div>
        </Modal.Body>

        {buttons && buttons.length > 0 && (
          <Modal.Footer>
            <div className="row flex-grow-1 gap-3">
              {buttons.map((item, index) => {
                return (
                  <div className="col p-0" key={index}>
                    <KTBSButton
                      borderRadius={5}
                      title={item?.title}
                      buttonTitleFontWeight="600"
                      color={getButtonColor(item?.type)}
                      buttonTitleColor={getButtonTitleColor(item?.type)}
                      onPress={() => {
                        if (item?.onClick) {
                          item?.onClick();
                        }
                      }}
                      iconElement={loading ? <div className="mr-8 spinner spinner-white"></div> : null}
                    />
                  </div>
                );
              })}
            </div>
          </Modal.Footer>
        )}
      </Modal>
    </div>
  );
}

export default AppDialog;
export const AppDialogHelper = {
  /**
   * Show dialog
   * @param {string} title Dialog title
   * @param {string} description Dialog description
   * @param {[{title: string, type: AppDialogButtonType, onClick: function}]} buttons Dialog buttons
   * @param {boolean} canClickOutsideToDismiss Dialog can click outside to dismiss
   */
  show: (
    title,
    description,
    buttons = [],
    canClickOutsideToDismiss = false,
    icon,
    customContent,
    size = '',
    loading = false,
  ) => {
    ref.current?.show(
      title,
      description,
      buttons,
      canClickOutsideToDismiss,
      icon,
      customContent,
      size,
      loading,
    );
  },
  /**
   * Hide dialog
   */
  hide: () => {
    ref.current.hide();
  },
};
